import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import appLogo from '../assets/branding/logo.png';

const progression = [
  'Garage', 'Small Office', 'Startup', 'Scale-up', 'Unicorn',
  'Public Company (IPO)', 'Global Tech Giant', 'Space Technology',
  'Artificial Super Intelligence', 'Interplanetary Corporation',
];

const loop = [
  { icon: '🛠️', label: 'Develop Product' },
  { icon: '🚀', label: 'Launch' },
  { icon: '📈', label: 'Gain Users' },
  { icon: '💰', label: 'Earn Revenue' },
  { icon: '🧑‍💼', label: 'Hire Team' },
  { icon: '🏢', label: 'Upgrade Office' },
  { icon: '🔬', label: 'Research Tech' },
  { icon: '🌍', label: 'Expand Worldwide' },
];

const departments = [
  { icon: '💻', name: 'Engineering' },
  { icon: '📣', name: 'Marketing' },
  { icon: '🤝', name: 'Sales' },
  { icon: '🎨', name: 'Design' },
  { icon: '🎧', name: 'Customer Support' },
  { icon: '🧑‍🤝‍🧑', name: 'HR' },
  { icon: '📊', name: 'Finance' },
  { icon: '⚖️', name: 'Legal' },
  { icon: '🔒', name: 'Security' },
  { icon: '🧪', name: 'Research' },
];

const researchTree = ['Programming', 'Cloud', 'Machine Learning', 'Artificial Intelligence', 'Quantum Computing', 'Fusion Power', 'Space Technology'];

const events = [
  { icon: '🔥', title: 'Data Breach', desc: 'Fix it immediately, or gamble and ignore it.' },
  { icon: '💼', title: 'Investor Meeting', desc: 'An investor wants in. Take the deal, or stay independent.' },
  { icon: '🤖', title: 'Rival Ships AI', desc: 'A competitor launches new AI. Undercut, out-build, or out-market them.' },
  { icon: '⚖️', title: 'Government Lawsuit', desc: 'Pay the fine, or fight it out in court.' },
];

const competitors = ['Pear', 'Macrosoft', 'Googlex', 'OmniAI', 'BlueCloud', 'NextOS', 'PixelTech'];

const liveStats = [
  { label: 'Users', value: '14,000,000', delta: '+12%' },
  { label: 'Revenue', value: '$24M/mo', delta: '+8%' },
  { label: 'Profit', value: '$8M/mo', delta: '+5%' },
  { label: 'Stock Price', value: '+18%', delta: '▲' },
];

const products = ['Calculator App', 'Notes App', 'Weather App', 'Messaging App', 'Cloud Storage', 'Operating System', 'AI Assistant', 'Quantum Computer', 'Self-driving Cars', 'Space Program'];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function SectionHeading({ eyebrow, title, subtitle, light }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <span className="pill">{eyebrow}</span>
      <h2 className={`mt-3 text-2xl font-black sm:text-3xl ${light ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
      {subtitle && (
        <p className={`mt-3 text-sm sm:text-base ${light ? 'text-slate-300' : 'text-slate-500'}`}>{subtitle}</p>
      )}
    </div>
  );
}

export default function Home() {
  const { user, isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-grid-glow">
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-bold uppercase tracking-widest text-amber-200 backdrop-blur">
                Tycoon Simulator
              </span>
              <p className="mt-5 text-sm font-bold uppercase tracking-widest text-blue-300">
                {isAuthenticated ? `${greeting()}, ${user?.name || user?.email || 'Founder'} 👋` : 'Year 2026 · You quit your job.'}
              </p>
              <h1 className="mt-3 text-4xl font-black leading-tight text-white sm:text-5xl">
                Build the next <span className="text-gold-gradient">trillion-dollar</span> tech company.
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300 sm:text-lg">
                Start with $5,000, a laptop, and a tiny office. Ship products, hire departments,
                out-research your rivals, and grow from garage startup to interplanetary corporation.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {isAuthenticated ? (
                  <span className="btn btn-gold">You're in — happy building 🚀</span>
                ) : (
                  <Link to="/register" className="btn btn-gold">Play Free — Create Account</Link>
                )}
                <Link to="/download" className="btn btn-outline-light">Download for iOS &amp; Android</Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-xs font-semibold text-slate-400">
                <span>🏛️ 10 Growth Stages</span>
                <span>🧪 7-Tier Research Tree</span>
                <span>🌌 Space-Age Endgame</span>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src={appLogo}
                alt="Tech Dynasty"
                className="h-56 w-56 rounded-[2rem] shadow-2xl ring-1 ring-white/10 sm:h-72 sm:w-72"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Progression roadmap */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Progression"
            title="From garage to galactic empire"
            subtitle="Ten stages of growth. Every milestone unlocks new mechanics, bigger offices, and bolder ambitions."
          />
          <div className="mt-10 overflow-x-auto pb-2">
            <div className="flex min-w-max items-center gap-2 px-1">
              {progression.map((stage, i) => (
                <React.Fragment key={stage}>
                  <div className="flex w-36 flex-col items-center rounded-2xl border border-slate-200 bg-white px-3 py-4 text-center shadow-sm">
                    <span className="mb-2 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-primary-dark)] text-xs font-black text-white">
                      {i + 1}
                    </span>
                    <span className="text-xs font-bold leading-snug text-slate-800">{stage}</span>
                  </div>
                  {i < progression.length - 1 && <span className="text-slate-300">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gameplay loop */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Core Loop"
            title="Simple to learn. Addictive to master."
            subtitle="A satisfying loop that always gives you a next move."
          />
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {loop.map((step, i) => (
              <div key={step.label} className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl text-xl" style={{ background: 'var(--brand-bg)' }}>
                  {step.icon}
                </div>
                <p className="text-xs font-black text-slate-800">{i + 1}. {step.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Product Roadmap"
            title="Ship everything from a calculator app to a space program"
            subtitle="Every product you launch grows your empire's reach — from pocket utilities to planet-scale infrastructure."
          />
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {products.map((p) => (
              <span key={p} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 shadow-sm">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Departments */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Departments"
            title="Hire beyond engineering"
            subtitle="Build out a full organization — every department unlocks new mechanics and strategies."
          />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {departments.map((d) => (
              <div key={d.name} className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl text-xl" style={{ background: 'var(--brand-bg)' }}>
                  {d.icon}
                </div>
                <p className="text-xs font-black text-slate-800">{d.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research tree + Live dashboard (dark) */}
      <section className="bg-grid-glow">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Research Tree"
            title="Chase long-term breakthroughs"
            subtitle="Climb the tech tree from programming basics to fusion power and space technology."
            light
          />
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {researchTree.map((r, i) => (
              <React.Fragment key={r}>
                <span className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold text-amber-100 backdrop-blur">
                  {r}
                </span>
                {i < researchTree.length - 1 && <span className="text-slate-500">→</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-16">
            <SectionHeading eyebrow="Live Dashboard" title="Watch your empire's numbers climb" light />
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {liveStats.map((s) => (
                <div key={s.label} className="game-card p-5 text-center">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
                  <p className="mt-2 text-xl font-black text-white sm:text-2xl">{s.value}</p>
                  <p className="mt-1 text-xs font-bold text-emerald-400">{s.delta}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-center text-xs text-slate-500">Illustrative in-game dashboard preview.</p>
          </div>
        </div>
      </section>

      {/* Events */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Random Events"
            title="No two playthroughs are the same"
            subtitle="Every few minutes, something happens — how you respond shapes your empire."
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {events.map((e) => (
              <div key={e.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-xl" style={{ background: 'var(--brand-bg)' }}>
                  {e.icon}
                </div>
                <p className="font-black text-slate-900">{e.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-slate-500">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitors & Acquisitions */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <span className="pill">Rivals</span>
              <h2 className="mt-3 text-2xl font-black text-slate-900">Outmaneuver fictional rivals</h2>
              <p className="mt-3 text-sm text-slate-500">
                Compete for users, talent, market share, and acquisitions against rival tech giants.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {competitors.map((c) => (
                  <span key={c} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 shadow-sm">
                    {c}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="pill">Acquisitions</span>
              <h2 className="mt-3 text-2xl font-black text-slate-900">Buy your way to the top</h2>
              <p className="mt-3 text-sm text-slate-500">
                Don't build everything yourself — acquire startups for their engineers, tech, and users.
              </p>
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <p className="font-black text-slate-900">Tiny AI Startup</p>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-600">$20M</span>
                </div>
                <p className="mt-2 text-xs text-slate-500">+ New engineers · + New technology · + Existing users</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="bg-grid-glow">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-black text-white sm:text-3xl">
            Ready to found your dynasty?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-sm text-slate-300 sm:text-base">
            Play free in your browser, or grab the app for iOS and Android.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {!isAuthenticated && (
              <Link to="/register" className="btn btn-gold">Create Free Account</Link>
            )}
            <Link to="/download" className="btn btn-outline-light">Get the App</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
